

import { Component, Vue, Prop, Ref, Emit } from "vue-property-decorator"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import { IntegrationColumn, IntegrationEventMapping, IntegrationSettings, Integration } from "@/types/integration"
import { Field, Template } from "@/types"
import cloneDeep from "lodash/cloneDeep"
import { ReceiveStateType } from "@/constants/receiveStateType"
import Guid from "@/scripts/guid"
import DeleteButton from "@/components/New/Page/PageComponents/DeleteButton.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { Getter } from "vuex-class"
import { BModal } from "bootstrap-vue"
import FieldMapping from "./FieldMapping.vue"

interface EventMappingOptions {
	createCase?: boolean,
	createPendingTriage?: boolean
}

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"delete-btn": DeleteButton,
		"field-mapping": FieldMapping
	}
})
export default class EventMapping extends Vue {
	@Ref() deleteConfirmModal!: BModal

	@Prop()
	value!: { [key: string]: any }

	@Prop({ default: () => [] })
	events!: IntegrationEventMapping[];

	@Prop({ default: false })
	allowCustomEvents!: boolean

	@Prop({ default: () => [] })
	templates!: Template[]

	@Prop()
	integration!: Integration

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter("getCaseName") caseName!: string;

	TenantFeatures = TenantFeatures

	ReceiveStateType = ReceiveStateType

	private renamingEvent: IntegrationEventMapping | null = null
	private renamingFeedback: string[] = []
	deletingEvent: IntegrationEventMapping | null = null

	get allEvents (): IntegrationEventMapping[] {
		return [...this.events ?? [], ...this.customEvents]
	}

	get supportCustomEvents (): boolean {
		return this.integration.inbound?.supportCustomEvents ?? false
	}

	get receiveStateType (): ReceiveStateType | null {
		return this.integration.inbound?.receiveStateType ?? null
	}

	get customEvents (): IntegrationEventMapping[] {
		let customEventKeys = this.events
			? Object.keys(this.value).filter(event => !this.events.find(e => e.key === event))
			: Object.keys(this.value)

		return customEventKeys.map(key => {
			let event = this.value[key]
			return {
				...event,
				key,
				title: event.title ?? key
			} as IntegrationEventMapping
		})
	}

	mounted () {
		if (!this.value) {
			this.$emit("input", {})
		}

		if (this.receiveStateType === ReceiveStateType.URL && Object.keys(this.value).length > 0) {
			let changeMade = false
			const val = cloneDeep(this.value)
			for (const key in Object.keys(val)) {
				const event = val[key]
				if (event && (!("receiveState" in event) || !event.receiveState)) {
					(val[key] as any).receiveState = Guid.newGuid()
					changeMade = true
				}
			}

			if (changeMade) {
				this.$emit("input", val)
			}
		}
	}

	valueSet (event: IntegrationEventMapping, templateId: string, options: EventMappingOptions) {
		const newValue = {
			...this.value,
			[event.key]: {
				...(this.value[event.key] ?? {}),
				templateId,
				title: event.title,
				createCase: options.createCase ?? this.value[event.key]?.createCase ?? false,
				createPendingTriage: options.createPendingTriage ?? this.value[event.key]?.createPendingTriage ?? false
			}
		}

		if (this.value[event.key]?.receiveState || this.receiveStateType === ReceiveStateType.URL) {
			newValue[event.key].receiveState = this.value[event.key]?.receiveState ?? Guid.newGuid()
		}
		this.$emit("input", newValue)
	}

	get customEventCount (): number {
		return Object.keys(this.value).filter(event => !this.events?.find(e => e.key === event) ?? []).length
	}

	addEvent () {
		const title = this.generateNewEventTitle()
		const newValue = {
			...this.value,
			[title]: {
				templateId: null,
				title,
				createCase: true,
				createPendingTriage: false
			}
		}
		if (this.receiveStateType === ReceiveStateType.URL) {
			newValue[title].receiveState = Guid.newGuid()
		}
		this.$emit("input", newValue)
	}

	deleteCustomEvent (event: IntegrationEventMapping | null) {
		if (!event) return

		const val = cloneDeep(this.value)
		delete val[event.key]
		this.$emit("input", val)
		this.deletingEvent = null
	}

	eventIsCustom (event: IntegrationEventMapping): boolean {
		return !this.events || !this.events.find(e => e.key === event.key)
	}

	getEventURL (event: IntegrationEventMapping): string {
		if (!event.receiveState) {
			console.error(`Event '${event.key}' does not have a receive state GUID`)
			return ""
		}
		let apiUrl = process.env.VUE_APP_HTTP_API_URI
		if (apiUrl?.startsWith("./")) {
			apiUrl = `${window.origin}${window.location.pathname}${apiUrl.substring(2)}`
		}
		const joinStr = apiUrl?.endsWith("/") ? "" : "/"
		return `${apiUrl}${joinStr}integration/${this.integration.id}/${event.receiveState}`
	}

	generateNewEventTitle (): string {
		let highestNum = 0
		this.customEvents.forEach(event => {
			let num = Number.parseInt(event.title.replace(this.integration.title, "").trim())
			if (num > highestNum) {
				highestNum = num
			}
		})

		return `${this.integration.title} ${highestNum + 1}`
	}

	async copyLinkToClipboard (event: IntegrationEventMapping) {
		if (!navigator.clipboard) {
			this.$bvToast.toast("Clipboard is not supported in your current environment", {
				title: "Could Not Copy To Clipboard",
				variant: "danger",
				solid: true
			})
			throw new Error("Clipboard is not supported in your current environment")
		}

		const link = this.getEventURL(event)

		await navigator.clipboard.writeText(link)
		this.$bvToast.toast(`'${link}'`, {
			title: "Copied To Clipboard",
			variant: "success",
			solid: true,
			toastClass: "text-break"
		})
	}

	private checkTitleValidity (event: IntegrationEventMapping, title: string) {
		const feedback: string[] = []

		if (title === event.title) {
			this.renamingFeedback = feedback
			return true
		}

		if (title.length === 0) {
			feedback.push("Title cannot be blank")
		} else if (this.allEvents.find(e => e.title === title)) {
			feedback.push("Title already exists")
		} else if (title.startsWith("$")) {
			feedback.push("Title cannot start with '$'")
		} else if (!title.match(/^[A-Za-z0-9$-\s]+$/)) {
			feedback.push("Title can only contain letters, numbers, spaces and dashes -")
		}

		this.renamingFeedback = feedback
	}

	private renameMapping (event: IntegrationEventMapping, newName: string) {
		const existingTitle = event.title

		if (existingTitle === newName) {
			this.renamingEvent = null
			return
		} else if (this.renamingFeedback.length > 0) {
			return
		}

		const newValue = cloneDeep(this.value)

		newValue[newName] = newValue[event.key]
		newValue[newName].title = newName
		delete newValue[event.key]

		this.$emit("input", newValue)
		this.renamingEvent = null
	}

	private onRenameMappingBlur (event: IntegrationEventMapping, newName: string) {
		setTimeout(() => {
			// ensure it wasn't cancel being clicked that triggered the blur
			if (this.renamingEvent) {
				this.renameMapping(event, newName)
			}
		}, 100)
	}

	@Emit("configureEventFieldMappings")
	private configureEventFieldMappings (event: IntegrationEventMapping) {
		return event
	}

	private getEventFieldMappingsCount (event: IntegrationEventMapping): number {
		return event.inFieldMapping ? Object.keys(event.inFieldMapping).length : 0
	}
}
